/**
 * Main orchestrating hook for the DataList component.
 *
 * Composes all 8 internal hooks in dependency order and returns the full
 * `UseDataListReturn` interface. Handles both server mode (all
 * filtering/sorting/pagination via API params) and client mode (inline data
 * processed locally through a search -> filter -> sort -> paginate pipeline).
 *
 * @see Plan section 3.9
 * @see Spec section 4.1
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

import type { DataListConfig, UseDataListReturn, SortState } from '../types';
import { useColumnResolver } from './useColumnResolver';
import { useListFilters } from './useListFilters';
import { useListSearch } from './useListSearch';
import { useListSorting } from './useListSorting';
import { useListPagination } from './useListPagination';
import { useFetchListData } from './useFetchListData';
import { useListSelection } from './useListSelection';
import { useCsvExport } from './useCsvExport';
import { dataListWorkflowRegistry } from '../workflow/registry';
import { handleActionComplete, handleBulkActionComplete } from '../workflow/handlers';
import { safeString } from '../utils/cellRenderer';
import { applyColumnTotalOverrides } from '../utils/totals';
import {
  DEFAULT_ROW_ID_FIELD,
  MANUAL_ROW_ORDER_UNAVAILABLE_MESSAGE,
} from '../constants';
import { applyRowOrder } from '../../../shared/configurable/layoutOverrides';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Type-aware value comparator matching `useListSorting` logic.
 *
 * - `null` / `undefined` values always sort to the end regardless of direction.
 * - Numbers are compared numerically.
 * - Strings are compared with `localeCompare`.
 * - Mixed or unknown types are coerced to strings for comparison.
 */
function compareValues(a: unknown, b: unknown, direction: 'asc' | 'desc'): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  let result: number;

  if (typeof a === 'number' && typeof b === 'number') {
    result = a - b;
  } else if (typeof a === 'string' && typeof b === 'string') {
    result = a.localeCompare(b);
  } else {
    result = safeString(a).localeCompare(safeString(b));
  }

  return direction === 'desc' ? -result : result;
}

/**
 * Applies client-side search filtering to a data array.
 *
 * Matches the same logic as `useListSearch`: case-insensitive substring
 * match across searchable columns.
 */
function applyClientSearch(
  data: Record<string, unknown>[],
  searchTerm: string,
  searchableColumnKeys: string[],
  getCellValue: (row: Record<string, unknown>, columnKey: string) => unknown,
): Record<string, unknown>[] {
  if (!searchTerm) return data;

  const lowerTerm = searchTerm.toLowerCase();

  return data.filter((row) =>
    searchableColumnKeys.some((colKey) => {
      const raw = getCellValue(row, colKey);
      if (raw == null) return false;
      return safeString(raw).toLowerCase().includes(lowerTerm);
    }),
  );
}

/**
 * Applies client-side filter matching to a data array.
 *
 * Supports both array filters (multiselect: checks inclusion) and scalar
 * filters (select: exact match).
 */
function applyClientFilters(
  data: Record<string, unknown>[],
  filterParams: Record<string, unknown>,
  getCellValue: (row: Record<string, unknown>, columnKey: string) => unknown,
): Record<string, unknown>[] {
  const activeFilters = Object.entries(filterParams);
  if (activeFilters.length === 0) return data;

  return data.filter((row) =>
    activeFilters.every(([key, filterValue]) => {
      const cellValue = getCellValue(row, key);

      // Array filter (multiselect): check if cell value is included
      if (Array.isArray(filterValue)) {
        return filterValue.length === 0 || filterValue.includes(cellValue);
      }

      // Scalar filter (select): exact match
      return String(cellValue) === String(filterValue);
    }),
  );
}

/**
 * Applies client-side multi-column sorting to a data array.
 *
 * Creates a shallow copy to avoid mutating the source array.
 */
function applyClientSort(
  data: Record<string, unknown>[],
  sortState: SortState[],
  getCellValue: (row: Record<string, unknown>, columnKey: string) => unknown,
): Record<string, unknown>[] {
  if (data.length === 0 || sortState.length === 0) return data;

  const sorted = [...data];
  sorted.sort((rowA, rowB) => {
    for (const { column, direction } of sortState) {
      const a = getCellValue(rowA, column);
      const b = getCellValue(rowB, column);
      const cmp = compareValues(a, b, direction);
      if (cmp !== 0) return cmp;
    }
    return 0;
  });

  return sorted;
}

/**
 * Applies client-side pagination (slice) to a data array.
 */
function applyClientPagination(
  data: Record<string, unknown>[],
  page: number,
  pageSize: number,
): Record<string, unknown>[] {
  const start = (page - 1) * pageSize;
  return data.slice(start, start + pageSize);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Main orchestrating hook that powers the entire DataList component.
 *
 * Composes all internal hooks in dependency order and wires data flow
 * differently depending on server vs client mode:
 *
 * **Server mode** (endpoint configured):
 * Search, sort, filter, and pagination params are sent to the API.
 * Data returned is already processed.
 *
 * **Client mode** (inline data):
 * All data is available locally. The hook applies search -> sort -> paginate
 * as a client-side pipeline.
 *
 * @param config - The full DataList configuration.
 * @returns The complete `UseDataListReturn` interface.
 */
export function useDataList(config: DataListConfig): UseDataListReturn {
  const isClientMode = Boolean(config.dataSource?.data) && !config.dataSource?.endpoint;

  // -----------------------------------------------------------------------
  // 1. Column resolver (for getCellValue + visibleColumns)
  //    We pass empty data here; totals are computed separately below
  //    with the final display data.
  // -----------------------------------------------------------------------

  const { visibleColumns, getCellValue } = useColumnResolver(
    config.columns,
    [], // totals computed separately below
    config.dataSource?.fieldMapping,
  );

  // -----------------------------------------------------------------------
  // 2. Filters
  // -----------------------------------------------------------------------

  const filters = useListFilters(config.toolbar?.filters);

  // -----------------------------------------------------------------------
  // 3. Search (debounced term + client-side filter capability)
  //    We pass empty data because we handle client-side filtering in the
  //    pipeline below. The hook gives us searchTerm + debouncedSearchTerm.
  // -----------------------------------------------------------------------

  const search = useListSearch(
    config.toolbar?.search,
    [], // client-side filtering handled in pipeline below
    config.columns,
    getCellValue,
  );

  // -----------------------------------------------------------------------
  // 4. Sorting (state + client-side sort capability)
  //    We pass empty data because we handle client-side sorting in the
  //    pipeline below. The hook gives us sortState + toggleSort + setSort.
  // -----------------------------------------------------------------------

  const sorting = useListSorting(
    config.sorting,
    [], // client-side sorting handled in pipeline below
    getCellValue,
    config.id,
  );

  // -----------------------------------------------------------------------
  // 5. Pagination
  //    For server mode: totalItems comes from the API response.
  //    For client mode: totalItems is the count after search+sort filtering.
  //    We track this in state and feed it to the pagination hook.
  // -----------------------------------------------------------------------

  const [clientTotalItems, setClientTotalItems] = useState(0);
  const [serverTotalItems, setServerTotalItems] = useState(0);

  const pagination = useListPagination(
    config.pagination,
    isClientMode ? clientTotalItems : serverTotalItems,
    config.id,
  );

  // -----------------------------------------------------------------------
  // 6. Fetch data
  //    Server mode: pass all params for server-side processing.
  //    Client mode: fetch all data (inline passthrough), pipeline below.
  // -----------------------------------------------------------------------

  const fetchResult = useFetchListData(config, {
    page: isClientMode ? 1 : pagination.page,
    pageSize: isClientMode ? 999999 : pagination.pageSize,
    sort: isClientMode ? [] : sorting.sortState,
    searchTerm: isClientMode ? '' : search.debouncedSearchTerm,
    filterParams: isClientMode ? {} : filters.filterParams,
  });

  // -----------------------------------------------------------------------
  // 7. Client-side data pipeline
  //    For client mode: raw data -> search -> filter -> sort -> paginate
  //    For server mode: data is returned as-is from the API
  // -----------------------------------------------------------------------

  // Resolve searchable column keys for client-side filtering
  const searchableColumnKeys = useMemo(() => {
    if (
      config.toolbar?.search?.searchableColumns &&
      config.toolbar.search.searchableColumns.length > 0
    ) {
      return config.toolbar.search.searchableColumns;
    }

    // Default: all columns whose type is 'text' or unspecified
    return Object.entries(config.columns)
      .filter(([, col]) => col.type === 'text' || col.type === undefined)
      .map(([key]) => key);
  }, [config.toolbar?.search?.searchableColumns, config.columns]);

  // Use a ref for getCellValue to avoid unnecessary recalculations
  const getCellValueRef = useRef(getCellValue);
  getCellValueRef.current = getCellValue;

  // -----------------------------------------------------------------------
  // Manual row order (data-row-ordering Tier 2)
  //    `dataSource.rowOrder` is stamped by the configurable engine's
  //    `applyLayoutOverrides` from a `reorderRows` rule. It is the order of
  //    *records*, so it is applied **only in client mode** as the base order
  //    (before search/filter and the interactive column sort): a user clicking
  //    a header still sorts on top, but the tenant's manual order is the
  //    initial/base sequence. For server/endpoint mode it is a documented
  //    no-op — a single page cannot be meaningfully hand-ordered — surfaced via
  //    `rowOrderUnavailableMessage` rather than misordering a page.
  // -----------------------------------------------------------------------
  const rowOrder = config.dataSource.rowOrder;
  const rowIdField = config.dataSource.rowIdField ?? DEFAULT_ROW_ID_FIELD;

  const getRowId = useCallback(
    (row: Record<string, unknown>): string | undefined => {
      const raw = row[rowIdField];
      // A stable id is the ERP/business key — a string or number. Anything
      // else (object/array/undefined) is treated as "no id" ⇒ unlisted.
      if (typeof raw !== 'string' && typeof raw !== 'number') return undefined;
      return String(raw);
    },
    [rowIdField],
  );

  const rowOrderUnavailableMessage =
    !isClientMode && rowOrder !== undefined && rowOrder.length > 0
      ? MANUAL_ROW_ORDER_UNAVAILABLE_MESSAGE
      : null;

  const processedData = useMemo(() => {
    if (!isClientMode) {
      return {
        data: fetchResult.data,
        totalItems: fetchResult.totalItems,
      };
    }

    // Step 0: Apply the tenant's manual row order as the base sequence. A no-op
    // (referential identity) when no `rowOrder` is configured, so the baseline
    // pipeline is byte-identical.
    const rawData = applyRowOrder(
      fetchResult.data,
      rowOrder,
      getRowId,
    ) as Record<string, unknown>[];

    // Step 1: Apply client-side search
    const searched = applyClientSearch(
      rawData,
      search.debouncedSearchTerm,
      searchableColumnKeys,
      getCellValueRef.current,
    );

    // Step 2: Apply client-side filters
    const filtered = applyClientFilters(
      searched,
      filters.filterParams,
      getCellValueRef.current,
    );

    // Step 3: Apply client-side sort
    const sorted = applyClientSort(
      filtered,
      sorting.sortState,
      getCellValueRef.current,
    );

    // Step 4: Compute total before pagination
    const totalItems = sorted.length;

    // Step 5: Apply client-side pagination
    const paged = applyClientPagination(sorted, pagination.page, pagination.pageSize);

    return { data: paged, totalItems };
  }, [
    isClientMode,
    fetchResult.data,
    fetchResult.totalItems,
    rowOrder,
    getRowId,
    search.debouncedSearchTerm,
    searchableColumnKeys,
    filters.filterParams,
    sorting.sortState,
    pagination.page,
    pagination.pageSize,
  ]);

  // Update total items for pagination hook
  useEffect(() => {
    if (isClientMode) {
      setClientTotalItems(processedData.totalItems);
    }
  }, [isClientMode, processedData.totalItems]);

  useEffect(() => {
    if (!isClientMode) {
      setServerTotalItems(fetchResult.totalItems);
    }
  }, [isClientMode, fetchResult.totalItems]);

  // For server mode, we need to feed totalItems into pagination.
  // The pagination hook was called with 0 for server mode above,
  // so we need a separate pagination instance or override totalPages.
  // Instead, we compute the effective pagination values:
  const effectiveTotalItems = isClientMode
    ? processedData.totalItems
    : fetchResult.totalItems;

  const effectiveTotalPages = Math.max(
    1,
    Math.ceil(effectiveTotalItems / pagination.pageSize),
  );

  const effectiveHasMore = pagination.page * pagination.pageSize < effectiveTotalItems;

  // -----------------------------------------------------------------------
  // 8. Column totals (on the current page display data)
  //    Native sums are computed over the displayed rows, then any
  //    authoritative `columnTotalOverrides` are overlaid (override wins per
  //    key; absent/NaN entries fall through to the computed sum).
  // -----------------------------------------------------------------------

  const columnTotals = useMemo(() => {
    const totals: Record<string, number> = {};

    for (const col of visibleColumns) {
      const hasTotal =
        col.total === true ||
        (typeof col.total === 'object' && col.total !== null);

      const isNumeric =
        (col.type ?? 'text') === 'number' || (col.type ?? 'text') === 'currency';

      if (!hasTotal || !isNumeric) continue;

      let sum = 0;
      for (const row of processedData.data) {
        const raw = getCellValueRef.current(row, col.key);
        const num = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isNaN(num)) {
          sum += num;
        }
      }

      totals[col.key] = sum;
    }

    return applyColumnTotalOverrides(totals, config.columnTotalOverrides);
  }, [visibleColumns, processedData.data, config.columnTotalOverrides]);

  // -----------------------------------------------------------------------
  // 9. Selection
  // -----------------------------------------------------------------------

  const selection = useListSelection();

  const isAllSelected = useMemo(() => {
    if (processedData.data.length === 0) return false;
    return processedData.data.every((row) => selection.isRowSelected(row));
  }, [processedData.data, selection.isRowSelected]);

  const toggleSelectAll = useCallback(() => {
    selection.toggleSelectAll(processedData.data);
  }, [selection.toggleSelectAll, processedData.data]);

  // -----------------------------------------------------------------------
  // 10. CSV export
  // -----------------------------------------------------------------------

  const csvExport = useCsvExport(
    config.toolbar?.export,
    visibleColumns,
    processedData.data,
    getCellValue,
    config.id,
  );

  // -----------------------------------------------------------------------
  // 11. Actions (row actions, bulk actions, row click)
  // -----------------------------------------------------------------------

  const executeRowAction = useCallback(
    async (actionId: string, row: Record<string, unknown>) => {
      const actionConfig = config.actions?.rowActions?.find((a) => a.id === actionId);
      const handlerKey = actionConfig?.handler ?? actionId;
      const handler = dataListWorkflowRegistry.getRowActionHandler(handlerKey);

      if (handler) {
        await handler(row, config);
        handleActionComplete(config.workflow, fetchResult.refresh, selection.clearSelection);
      }
    },
    [config, fetchResult.refresh, selection.clearSelection],
  );

  const executeBulkAction = useCallback(
    async (actionId: string) => {
      const actionConfig = config.actions?.bulkActions?.find((a) => a.id === actionId);
      const handlerKey = actionConfig?.handler ?? actionId;
      const handler = dataListWorkflowRegistry.getBulkActionHandler(handlerKey);

      if (handler) {
        await handler(selection.selectedRows, config);
        handleBulkActionComplete(
          config.workflow,
          fetchResult.refresh,
          selection.clearSelection,
        );
      }
    },
    [config, selection.selectedRows, fetchResult.refresh, selection.clearSelection],
  );

  const handleRowClick = useCallback(
    (row: Record<string, unknown>) => {
      if (config.actions?.rowClick?.handler) {
        const handler = dataListWorkflowRegistry.getRowClickHandler(
          config.actions.rowClick.handler,
        );
        if (handler) {
          handler(row);
        }
      }
    },
    [config.actions?.rowClick?.handler],
  );

  // -----------------------------------------------------------------------
  // 12. Collapse state
  // -----------------------------------------------------------------------

  const [isCollapsed, setIsCollapsed] = useState(
    config.display?.collapsible ? (config.display?.defaultCollapsed ?? false) : false,
  );

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  // -----------------------------------------------------------------------
  // 13. Reset page to 1 when search or filters change
  // -----------------------------------------------------------------------

  const prevSearchRef = useRef(search.debouncedSearchTerm);
  const prevFilterRef = useRef(filters.filterParams);

  useEffect(() => {
    if (prevSearchRef.current !== search.debouncedSearchTerm) {
      prevSearchRef.current = search.debouncedSearchTerm;
      pagination.setPage(1);
    }
  }, [search.debouncedSearchTerm, pagination.setPage]);

  useEffect(() => {
    if (prevFilterRef.current !== filters.filterParams) {
      prevFilterRef.current = filters.filterParams;
      pagination.setPage(1);
    }
  }, [filters.filterParams, pagination.setPage]);

  // -----------------------------------------------------------------------
  // 14. Reset page to 1 when the sort changes (server mode only)
  //    Sorting re-queries the whole result set, so a user sorting while on
  //    page 3 must not be stranded on an out-of-range page. Guarded against
  //    the initial render (and the `persistPage` restore) via a signature ref
  //    so it only fires on an actual sort change. In client mode the page is
  //    clamped by the pagination pipeline, so no reset is needed.
  // -----------------------------------------------------------------------

  const sortSignature = JSON.stringify(sorting.sortState);
  const prevSortSignatureRef = useRef(sortSignature);

  useEffect(() => {
    if (prevSortSignatureRef.current !== sortSignature) {
      prevSortSignatureRef.current = sortSignature;
      if (!isClientMode) pagination.setPage(1);
    }
  }, [sortSignature, isClientMode, pagination.setPage]);

  // -----------------------------------------------------------------------
  // Return the full UseDataListReturn interface
  // -----------------------------------------------------------------------

  return {
    // Data
    data: processedData.data,
    totalItems: effectiveTotalItems,
    isLoading: fetchResult.isLoading,
    isInitialLoading: fetchResult.isInitialLoading,
    error: fetchResult.error,
    refresh: fetchResult.refresh,

    // Columns
    visibleColumns,
    getCellValue,

    // Sorting
    sortState: sorting.sortState,
    toggleSort: sorting.toggleSort,
    setSort: sorting.setSort,

    // Pagination
    page: pagination.page,
    pageSize: pagination.pageSize,
    totalPages: effectiveTotalPages,
    setPage: pagination.setPage,
    setPageSize: pagination.setPageSize,
    loadMore: pagination.loadMore,
    hasMore: effectiveHasMore,

    // Search
    searchTerm: search.searchTerm,
    setSearchTerm: search.setSearchTerm,

    // Filters
    filterValues: filters.filterValues,
    setFilterValue: filters.setFilterValue,
    resetFilters: filters.resetFilters,

    // Selection
    selectedRows: selection.selectedRows,
    isAllSelected,
    toggleRowSelection: selection.toggleRowSelection,
    toggleSelectAll,
    clearSelection: selection.clearSelection,
    isRowSelected: selection.isRowSelected,

    // Actions
    executeRowAction,
    executeBulkAction,
    handleRowClick,

    // Display
    isCollapsed,
    toggleCollapsed,

    // Manual row order (Tier 2) — degrade message for unsupported sources
    rowOrderUnavailableMessage,

    // Totals
    columnTotals,

    // Export
    exportCsv: csvExport.exportCsv,

    // Config
    config,
  };
}
